import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { resolveInternalHref } from "@/lib/seo/internalHref";

import {
  LEAGUE_CLUSTERS,
  MARKET_CLUSTERS,
  GUIDES,
  type ClusterItem,
} from "@/lib/seoClusters";

interface LinkGroup {
  heading: string;
  basePath: string;
  items: ClusterItem[];
}

interface InternalLinksProps {
  /** Optional: exclude a specific slug (e.g. the current hub). */
  excludeSlug?: string;
  title?: string;
}

/** Hand-picked, high-intent landing pages surfaced on every hub. */
const POPULAR: { label: string; path: string }[] = [
  { label: "Predictions Today", path: "/predictions/today" },
  { label: "Bonus Picks Today", path: "/predictions/bonus-picks-today" },
  { label: "Football Predictions Nigeria", path: "/predictions/nigeria-today" },
  { label: "Best Prediction Site", path: "/best-football-prediction-site" },
  { label: "Jackpot Predictions", path: "/jackpot-predictions" },
  { label: "NFL Predictions", path: "/nfl-predictions" },
  { label: "NBA Predictions", path: "/nba-predictions" },
  { label: "MLB Predictions", path: "/mlb-predictions" },
  { label: "NHL Predictions", path: "/nhl-predictions" },
];

/**
 * Topical-cluster internal link block. Surfaces league, market and guide hubs
 * plus high-intent landing pages so crawlers and users can move across the
 * cluster graph from any hub page.
 */
export function InternalLinks({ excludeSlug, title = "Explore More Predictions" }: InternalLinksProps) {
  const groups: LinkGroup[] = [
    { heading: "Leagues", basePath: "/leagues", items: LEAGUE_CLUSTERS },
    { heading: "Markets", basePath: "/markets", items: MARKET_CLUSTERS },
    { heading: "Betting Guides", basePath: "/guides", items: GUIDES },
  ];

  return (
    <section aria-label={uiText("Related predictions and guides")} className="space-y-4">
      <h2 className="text-xl font-semibold">{title}</h2>
      <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {groups.map((group) => (
          <Card key={group.heading} className="flex h-full flex-col p-4">
            <h3 className="mb-3 text-sm font-semibold text-muted-foreground">{uiText(group.heading)}</h3>
            <ul className="space-y-2">
              {group.items
                .filter((item) => item.slug !== excludeSlug)
                .map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={resolveInternalHref(`${group.basePath}/${item.slug}`)}
                      className="group flex min-h-8 items-center justify-between gap-2 text-sm transition-colors hover:text-primary"
                    >
                      <span className="truncate">{uiText(item.label)}</span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
            </ul>
          </Card>
        ))}
        <Card className="flex h-full flex-col p-4">
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">{uiText("Popular Pages")}</h3>
          <ul className="space-y-2">
            {POPULAR.map((item) => (
              <li key={item.path}>
                <Link
                  to={resolveInternalHref(item.path)}
                  className="group flex min-h-8 items-center justify-between gap-2 text-sm transition-colors hover:text-primary"
                >
                  <span className="truncate">{uiText(item.label)}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <Link to="/explore" className="text-primary hover:underline">
           {uiText("Browse the full Explore hub →")} </Link>
        <Link to="/fixtures" className="text-primary hover:underline">
           {uiText("Browse the fixtures archive →")} </Link>
        <Link to="/ai-predictions" className="text-primary hover:underline">
           {uiText("See all AI football predictions →")} </Link>
      </p>
    </section>
  );
}
